import { fmtDate, projectCode, type BoardItem, type BoardShot, type HistoryEntry } from "@boem/core/shared";
import { useEffect, useState, type ReactNode } from "react";
import { api, ApiError } from "../api.ts";
import type { PanelKind } from "../panels.ts";
import { AiChip, Linkify } from "../util.tsx";
import { Icon } from "./Icons.tsx";
import { SOURCE_LABEL } from "./ItemModals.tsx";
import { NoteThumbs } from "./Media.tsx";
import { allNumbers, itemNo } from "./Structure.tsx";

// The head of a cascading panel (docs/design 4.3): what it is, where it came from, pin, Open as
// board, close; on a phone the trail back to the board. Sticky at the top of the panel.

type Numbered = Pick<BoardItem, "num" | "key" | "kind" | "projectNo" | "title">;

/** The number people say for a source, in the bridge: 03 (a project), 03-01, #188. */
export const bridgeLabel = (it: Pick<BoardItem, "num" | "key" | "kind" | "projectNo">) =>
  it.kind === "project" ? (it.projectNo != null ? projectCode(it.projectNo) : `#${it.num}`) : (it.key?.label ?? `#${it.num}`);

/** What a folded panel (a spine) says, turned on its side: "03 Delivery app", "03-01". */
export const spineLabel = (it: Numbered | null, num: number) =>
  !it ? `#${num}` : it.kind === "project" ? `${it.projectNo != null ? projectCode(it.projectNo) : `#${it.num}`} ${it.title}` : bridgeLabel(it);

/** One step of the phone trail: "Board › 03 Delivery app › #01 › #06". */
export interface Crumb {
  label: string;
  /** jump back to this step (the current one has none) */
  onClick?: () => void;
}

/**
 * The trail to a panel: the board, then each panel up to it. A project panel is "03 Delivery app";
 * an item is numbered in the nearest project to its left (#01 inside 03), else 03-01 or #188.
 */
export function trail(boardLabel: string, chain: { kind: PanelKind; item: Numbered | null; num: number }[], boardScope: number | null): string[] {
  let scope = boardScope;
  const out = [boardLabel];
  for (const p of chain) {
    if (p.kind === "project") {
      out.push(spineLabel(p.item, p.num));
      scope = p.num;
    } else {
      const no = p.item ? itemNo(p.item, scope) : `#${p.num}`;
      out.push(p.kind === "notes" ? `Notes ${no}` : no);
    }
  }
  return out;
}

export function Crumbs({ crumbs }: { crumbs: Crumb[] }) {
  return (
    <nav className="sp-crumbs" aria-label="Where this panel sits">
      {crumbs.map((c, i) => (
        <span key={i} className="sp-crumb">
          {i > 0 && <span aria-hidden="true"> &rsaquo; </span>}
          {c.onClick ? (
            <button type="button" onClick={c.onClick}>
              {c.label}
            </button>
          ) : (
            <b aria-current="page">{c.label}</b>
          )}
        </span>
      ))}
    </nav>
  );
}

export interface PanelHeadProps {
  kind: PanelKind;
  num: number;
  /** null: not on the board or in the archive (any more) */
  item: BoardItem | null;
  pinned: boolean;
  /** the "From 02-06 Return-to-vendor…" line: the card it was opened from */
  from: { label: string; title: string } | null;
  crumbs: Crumb[];
  /** a line under the title: progress for a project, where an item sits */
  meta?: ReactNode;
  onBack: () => void;
  onClose: () => void;
  onPin: () => void;
  /** on a phone the board is under the sheets, so Reset is here too */
  onReset?: () => void;
  onScrollToSource?: () => void;
  /** a project panel: make it the board on screen, its filters carried over */
  onOpenAsBoard?: () => void;
  /** an item panel: switch to the board it is on, at its card */
  onGoTo?: () => void;
}

export function PanelHeader(p: PanelHeadProps) {
  const it = p.item;
  const project = p.kind === "project";
  // a notes panel: "Notes · 03-01 · #48 · 9 notes"
  const n = it?.notes.length ?? 0;
  const eyebrow = !it
    ? `${project ? "Project" : p.kind === "notes" ? "Notes" : "Item"} · #${p.num}`
    : project
      ? `Project ${it.projectNo != null ? `${projectCode(it.projectNo)} · ` : ""}#${it.num}`
      : p.kind === "notes"
        ? `Notes · ${[it.key?.label, `#${it.num}`].filter(Boolean).join(" · ")} · ${n} note${n === 1 ? "" : "s"}`
        : `Item · ${allNumbers(it)}`;
  return (
    <div className="sp-head">
      <Crumbs crumbs={p.crumbs} />
      <div className="sp-top">
        <button type="button" className="chip sm sp-back" title="Back one step" onClick={p.onBack}>
          <Icon name="back" /> Back
        </button>
        <span className="sp-eyebrow" title={eyebrow}>
          {eyebrow}
        </span>
        <span className="sp-acts">
          <button
            type="button"
            className="chip sm sp-pin"
            aria-pressed={p.pinned}
            title={p.pinned ? "Unpin: it closes on Reset like the others" : "Pin: keep it through Reset, and open it again after sign-in"}
            onClick={p.onPin}
          >
            <Icon name="pin" /> {p.pinned ? "Pinned" : "Pin"}
          </button>
          {p.onOpenAsBoard && (
            <button type="button" className="chip sm" title="Make this project the board on screen; its filters come along" onClick={p.onOpenAsBoard}>
              Open as board
            </button>
          )}
          {p.onGoTo && (
            <button type="button" className="chip sm" title={it?.project ? `Switch the board to ${it.project.title}, at this item` : "Show it on the main board"} onClick={p.onGoTo}>
              {it?.project ? "Go to its board" : "Go to it"}
            </button>
          )}
          {p.onReset && (
            <button type="button" className="chip sm sp-reset" title="Close every unpinned panel; back to the main board, filters cleared" onClick={p.onReset}>
              Reset
            </button>
          )}
          <button type="button" className="chip sm sp-close" aria-label="Close this panel and those right of it" title="Close this panel (and those right of it)" onClick={p.onClose}>
            <Icon name="close" />
          </button>
        </span>
      </div>
      {p.from ? (
        <div className="sp-from">
          <span>From</span>
          <span className="k">{p.from.label}</span>
          <span className="t" title={p.from.title}>
            {p.from.title}
          </span>
          {p.onScrollToSource && (
            <button type="button" className="chip sm" title={`Show ${p.from.label} in the column it is in`} onClick={p.onScrollToSource}>
              Scroll to it
            </button>
          )}
        </div>
      ) : (
        p.pinned && (
          <div className="sp-from">
            <span className="k">Pinned</span>
            <span className="t">stays through Reset, back after sign-in</span>
          </div>
        )
      )}
      <h2 className="sp-title">{it ? it.title : `#${p.num} isn't on the board or in the archive.`}</h2>
      {p.meta && <div className="sp-meta">{p.meta}</div>}
    </div>
  );
}

/** An item's History, inline under its card in an item panel. `version` changes when the item does. */
/** `shots`: the item's screenshots, so a note's line shows the ones added with it. */
export function HistoryList({ num, version, shots = [] }: { num: number; version: string; shots?: BoardShot[] }) {
  const [rows, setRows] = useState<HistoryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    api.history(num).then(
      (r) => live && (setRows(r), setError(null)),
      (e) => live && setError(e instanceof ApiError ? e.message : "Couldn't load the history."),
    );
    return () => {
      live = false;
    };
  }, [num, version]);
  return (
    <section className="sp-history" aria-label="History">
      <div className="sp-section">History</div>
      {!rows && !error && <p className="count-line">Loading&hellip;</p>}
      {error && <p className="msg err">{error}</p>}
      {rows && (
        <ol className="hist">
          {rows.map((r, i) => (
            <li key={i}>
              <span className="hist-when">{fmtDate(r.at)}</span>
              <span className="hist-what">
                <b>{r.actor ?? "Someone"}</b> {r.agent && <AiChip agent={r.agent} />} <Linkify text={r.text} />
                <NoteThumbs shots={shots} noteId={r.noteId} />
              </span>
              <span className="hist-src">{SOURCE_LABEL[r.source] ?? r.source}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
